import { useRouter } from 'next/router';
import { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

import { useClient, useSessionStorage } from '../../hooks';
import { useNavigationWithQuery } from '../useNavigationWithQuery';

type RedirectQueryStrings = {
  access_token: string;
  id_token: string;
  state: string;
  refresh_token?: string;
  mfa?: string;
};

export function useRedirectToClient() {
  const { redirectURL, tenancy: TENANCY, clientState } = useClient();
  const { getSessionItem } = useSessionStorage();
  const { navigateTo } = useNavigationWithQuery();

  const router = useRouter();
  const [redirectingToClient, setRedirectingToClient] = useState(false);

  const checkIfInviteSetup = () => {
    const inviteToken = getSessionItem('invite');
    if (inviteToken && clientState === inviteToken) return true;
    return false;
  };

  const redirectToClient = async () => {
    if (checkIfInviteSetup()) {
      navigateTo({ path: 'inviteConfirmed' });
    }
    if (router.query && router.query.redirectTo) {
      const query = router.query;
      const { redirectTo } = query;
      delete query.redirectTo;
      router.push({ pathname: `/${redirectTo}`, query });
    }
    const fullRedirectURL = redirectURL.concat(
      TENANCY === 'APS' ? '' : '#/callback'
    );
    setRedirectingToClient(true);

    const session = await fetchAuthSession({ forceRefresh: true });

    const accessToken = session.tokens?.accessToken?.toString() || '';
    const idToken = session.tokens?.idToken?.toString() || '';

    if (!accessToken || !idToken) {
      return;
    }

    const queryStringParams: RedirectQueryStrings = {
      access_token: accessToken,
      id_token: idToken,
      state: clientState || '',
    };

    window.location.replace(
      `${fullRedirectURL}?${(
        Object.keys(queryStringParams) as Array<keyof typeof queryStringParams>
      )
        .map((qs) => qs.concat('=', queryStringParams[qs]!))
        .join('&')}`
    );
  };

  return { redirectToClient, redirectingToClient };
}
